import { isDevelopmentEnv, isStagingEnv } from '@mc/client-env'
import { logger } from '@mc/next-logger'

/**
 * Safely retrieves a value from sessionStorage.
 * Returns null if sessionStorage is unavailable or an error occurs.
 * Errors are only logged in development and staging environments.
 *
 * @param key - The key to retrieve from sessionStorage.
 * @returns The value corresponding to the key, or null if an error occurs.
 */
export const safeGetSessionStorageItem = (key: string): string | null => {
  if (typeof window === 'undefined' || typeof sessionStorage === 'undefined')
    return null

  try {
    return sessionStorage.getItem(key)
  } catch (err) {
    if (isDevelopmentEnv() || isStagingEnv()) {
      logger.error(`Error reading sessionStorage key "${key}":`, err)
    }
    return null
  }
}

/**
 * The same as `safeGetSessionStorageItem` but for multiple keys.
 *
 * @param keys An array of sessionStorage keys to retrieve.
 * @returns An array of values corresponding to each key, or null for each key if sessionStorage is unavailable or an error occurs.
 */
export const safeGetSessionStorageItems = (
  keys: string[],
): Array<string | null> => keys.map((key) => safeGetSessionStorageItem(key))
